import * as React from 'react'
import {
  Box,
  Typography,
  InputAdornment,
  Button,
  Stack,
  AlertTitle,
  Avatar,
  Paper,
  TextField,
  Tabs,
  Tab,
  MenuItem,
  Select,
  useMediaQuery,
} from '@mui/material'
import {
  addDays,
  dateDescription,
  diffDays,
  lastTime,
  resetTime,
} from '../../../core/functions/dateTime'
import NumberFunctions from '../../../core/functions/NumberFunctions'
import { useCheckoutOrders } from '../../../core/hooks/useCheckoutOrders'
import {
  ICheckoutOrdersFilter,
  TCheckoutOrder,
  TCheckoutOrderPagination,
} from '../../../core/types/CheckoutOrder'
import CheckoutOrderDetails from './CheckoutOrderDetails'
import { ExportToFile } from '../ExportToFile'
import { ExportDialog } from '../ExportDialog'
import type { ReportType, FileType } from '../ExportDialog'
import {
  ExportFileTypeEnum,
  ReportDownloadController,
} from '../../../core/controllers/ReportDownloadController'
import { useDownloadFile } from '../../../core/hooks/useDownloadFile'
import SuccessSnackbar from '../../../components/Snackbar/SuccessSnackbar'
import ErrorSnackbar from '../../../components/Snackbar/ErrorSnackbar'
import LoadingBackdrop from '../../../components/Loading/LoadingBackdrop'
import { AuthContext } from '../../../core/context/AuthContext'
import { useCurrentTenantBank } from '../../../core/hooks/useCurrentTenantBank'
import 'dayjs/locale/pt-br'
import { AdvancedFilters } from '../AdvancedFilters'
import { useProductsSimple } from '../../../core/hooks/useProductsSimple'
import { useState } from 'react'
import { useAffiliatedsSimple } from '../../../core/hooks/useAffiliatedsSimple'
import SimpleCard from '../../../components/Cards/SimpleCard'
import { useInternationalSalesEnabled } from '../../../core/hooks/useInternationalSalesEnabled'
import { CurrencyToggle } from '../../../components/Filters/CurrencyToggle'
import DualCurrencyTotals from '../../../components/Cards/DualCurrencyTotals'
import TitlePageTypography from '../../../components/Typographys/TitlePageTypography'
import SubtitlePageTypography from '../../../components/Typographys/SubtitlePageTypography'
import DataTable, {
  IDataTableColumn,
} from '../../../components/Tables/DataTable'
import SelectInput from '../../../components/SelectInput/SelectInput'
import { a11yProps } from '../../../components/Tabs'
import {
  TCheckoutOrderReport,
  TCheckoutOrderReportTab,
} from '../../../core/types/Order'
import OrderFunctions from '../../../core/functions/OrderFunctions'
import { AsaasSeal } from '../../../components/AsaasSeal/AsaasSeal'
import {
  CHECKOUT_TAB_ABANDONED,
  CHECKOUT_TAB_ALL,
  CHECKOUT_TAB_APPROVED,
  CHECKOUT_TAB_CANCELED,
  CHECKOUT_TAB_OPENED,
  CHECKOUT_TAB_RECONCILIATION,
  CheckoutOrderDataTableColumns,
} from './CheckoutOrderConsts'
import CheckoutOrdersMobile from './CheckoutOrdersMobile'
import { TLostSale } from '../../../core/types/LostSale'
import { AbandonedAdvancedFilters } from '../AdvancedFilters/AbandonedAdvancedFilters'
import AbandonedItemMobile from '../Abandoned/AbandonedItemMobile'
import OrderController from '../../../core/controllers/OrderController'
import Hosts from '../../../core/functions/hosts'
import AlertDefault from '../../../components/Alerts'
import AnticipationReconciliationTab, {
  AnticipationReconciliationTotals,
} from './AnticipationReconciliation'
import { useAnticipationReconciliations } from '../../../core/hooks/useAnticipationReconciliations'

export default function CheckoutOrdersPage() {
  const { getTenantId, isUserSysAdmin } = React.useContext(AuthContext)
  const isSysAdmin = isUserSysAdmin()
  const internationalSalesEnabled = useInternationalSalesEnabled()

  const [windowSize, setWindowSize] = useState([
    window.innerWidth,
    window.innerHeight,
  ])

  const [reportType, setReportType] = useState<ReportType>('default')
  const [tabIndex, setTabIndex] = React.useState(0)
  const [page, setPage] = useState(0)
  const [reconciliationPage, setReconciliationPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [copied, setCopied] = useState(false)
  const [type, setType] = useState<TCheckoutOrderReportTab>('approved')
  const [report, setReport] = useState<TCheckoutOrderReport>('all')
  const [current, setCurrent] = useState<TCheckoutOrder | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errorSending, setErrorSending] = useState('')

  const [openFilter, setOpenFilter] = useState(false)
  const [customerWithAddress, setCustomerWithAddress] = useState(false)

  const [filters, setFilters] = useState<ICheckoutOrdersFilter>({
    startDate: resetTime(addDays(-29)),
    endDate: lastTime(),
    sellerType: 0,
    lastDays: 30,
    affiliateds: [],
    payments: [],
    products: [],
    status: [],
    currency: 'brl',
  })

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('md')
  )

  const handleDownloadBoleto = async (codeId: string, chargeId: string) => {
    const boletoURL = await OrderController.getPaymentLink({ codeId, chargeId })
    if (!boletoURL.success) {
      setErrorSending(boletoURL.error || 'Erro ao copiar código')
      return
    }
    window.open(boletoURL.data, '_blank')
  }

  const handleCopyPixCode = async (codeId: string, chargeId: string) => {
    const pixCode = await OrderController.getPaymentLink({ codeId, chargeId })
    if (!pixCode.success) {
      setErrorSending(pixCode.error || 'Erro ao copiar código PIX')
      return
    }
    Hosts.copyTextToClipboard(pixCode.data)
    setCopied(true)
  }

  const { data: tenantBank } = useCurrentTenantBank({
    tenantId: getTenantId(),
  })
  const { data: products, loading: loadingProducts } = useProductsSimple({
    initialExecute: true,
    includeAffiliatedProducts: true,
  })
  const { data: affiliateds, loading: loadingAffiliateds } =
    useAffiliatedsSimple({ initialExecute: true })

  const { loading, data, error, execute, clear } = useCheckoutOrders({
    page,
    rows: rowsPerPage,
    search,
    type,
    report,
    filters,
  })

  const isReconciliation = tabIndex === CHECKOUT_TAB_RECONCILIATION
  const reconciliation = useAnticipationReconciliations({
    enabled: isSysAdmin && isReconciliation,
    page: reconciliationPage,
    rows: rowsPerPage,
    search,
    startDate: filters.startDate,
    endDate: filters.endDate,
    products: filters.products,
    affiliateds: filters.affiliateds,
  })

  const dataTableColumns = CheckoutOrderDataTableColumns({
    setCopied,
    onDownloadBoleto: handleDownloadBoleto,
    onCopyPix: handleCopyPixCode,
    execute,
  })

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (page > 0) {
      setPage(0)
    }

    let nextType: TCheckoutOrderReportTab = type

    switch (newValue) {
      case CHECKOUT_TAB_APPROVED:
        if (filters.status && filters.status.length > 0) {
          setFilters({ ...filters, status: [] })
        }
        nextType = 'approved'
        break

      case CHECKOUT_TAB_ALL:
        if (filters.status && filters.status.length > 0) {
          setFilters({ ...filters, status: [] })
        }
        nextType = 'all'
        break

      case CHECKOUT_TAB_OPENED:
        setFilters({ ...filters, status: ['pending'] })
        nextType = 'opened'
        break

      case CHECKOUT_TAB_ABANDONED:
        nextType = 'abandoned'
        break

      case CHECKOUT_TAB_CANCELED:
        setFilters({
          ...filters,
          status: ['canceled', 'chargedback', 'refund_claimed'],
        })
        nextType = 'canceled'
        break
    }

    // ✅ split e expectedRelease só podem existir em "approved"
    if (
      nextType !== 'approved' &&
      (reportType === 'split' || reportType === 'expectedRelease')
    ) {
      setReportType('default')
    }

    setType(nextType)
    setTabIndex(newValue)
  }

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (rows: number) => {
    setRowsPerPage(rows)
    setPage(0)
    setReconciliationPage(0)
  }

  const handleChangeItem = (checkoutOrder: TCheckoutOrder | TLostSale) => {
    setCurrent(checkoutOrder as TCheckoutOrder)
    setOpen(true)
  }

  const handleClose = () => {
    setCurrent(undefined)
    setOpen(false)
  }

  const handleDownloadFile = (typeFile: ExportFileTypeEnum) => {
    if (type === 'abandoned') {
      return ReportDownloadController.getLostSales({
        fileType: typeFile,
        search,
        lastDays: filters.lastDays,
        type: '',
        report,
      })
    }

    // ✅ split (novo endpoint) - apenas "approved"
    if (reportType === 'split') {
      if (type !== 'approved') {
        return Promise.resolve({
          code: 400,
          error: 'Relatório split disponível apenas na aba Aprovadas.',
        })
      }

      return ReportDownloadController.ordersGetAllSplit({
        fileType: typeFile,
        startDate: filters.startDate ?? null,
        endDate: filters.endDate ?? null,
        search, // opcional no backend
      })
    }

    // ✅ expectedRelease (novo endpoint) - apenas "approved"
    if (reportType === 'expectedRelease') {
      if (type !== 'approved') {
        return Promise.resolve({
          code: 400,
          error:
            'Relatório de previsão de liberação disponível apenas na aba Aprovadas.',
        })
      }

      return ReportDownloadController.getExpectedReleaseFile({
        fileType: typeFile,
        search,
        type,
        report,
        affiliateds: filters.affiliateds ?? [],
        payments: filters.payments ?? [],
        products: filters.products ?? [],
        sellerType: filters.sellerType ?? null,
      })
    }

    // padrão / com endereço (endpoint existente)
    return ReportDownloadController.ordersGetAll({
      ...filters,
      fileType: typeFile,
      search,
      type,
      report,
      hasAddress: reportType === 'withAddress',
    })
  }

  const handlerDownloadingError = (e: string) => setErrorSending(e)

  const getFileName = (type: ExportFileTypeEnum) =>
    `report_${new Date().toISOString().replace(':', '_').replace('.', '_')}.${
      type === ExportFileTypeEnum.XLS ? 'xlsx' : 'csv'
    }`

  const { download, downloading } = useDownloadFile({
    apiDefinition: handleDownloadFile,
    onError: handlerDownloadingError,
    getFileName,
  })

  const downloadCallback = React.useCallback(
    (type: ExportFileTypeEnum) => download(type),
    [download]
  )

  const handleConvertToXLS = async () => {
    if (isValidDownloadReport()) downloadCallback(ExportFileTypeEnum.XLS)
  }

  const handleConvertToCSV = async () => {
    if (isValidDownloadReport()) downloadCallback(ExportFileTypeEnum.CSV)
  }

  const isValidDownloadReport = (): boolean => {
    if (filters.startDate === null || filters.startDate === undefined) {
      setErrorSending('Necessário informar o período inicial.')
      return false
    }

    if (filters.endDate === null || filters.endDate === undefined) {
      setErrorSending('Necessário informar um período final.')
      return false
    }

    if (diffDays(filters.endDate, filters.startDate) > 30) {
      setErrorSending('Não informar um período maior que 30 dias')
      return false
    }

    if (
      (reportType === 'split' || reportType === 'expectedRelease') &&
      type !== 'approved'
    ) {
      setErrorSending('Esse relatório está disponível apenas na aba Aprovadas.')
      return false
    }

    return true
  }

  const isTenantBank = () => {
    return (
      tenantBank &&
      tenantBank !== null &&
      tenantBank.accountNumber &&
      tenantBank.accountNumber !== null &&
      tenantBank.bankCode &&
      tenantBank.bankCode !== null
    )
  }

  const handleOpenFilter = () => setOpenFilter(true)

  const handleCloseFilter = () => setOpenFilter(false)

  const renderItemMobile = (
    item: TCheckoutOrder | TLostSale,
    index: number
  ) => {
    const lostSale = item as TLostSale
    return (
      <AbandonedItemMobile
        item={lostSale}
        index={index}
        setCopied={setCopied}
      />
    )
  }

  React.useEffect(() => {
    window.addEventListener('resize', (ev) => {
      setWindowSize([window.innerWidth, window.innerHeight])
    })
  }, [])

  React.useEffect(() => {
    if (data.rowsPerPage !== rowsPerPage) {
      setRowsPerPage(data.rowsPerPage)
    }
  }, [data, rowsPerPage])

  return (
    <>
      {isSmallScreen && type !== 'abandoned' && (
        <CheckoutOrdersMobile
          startDate={filters.startDate}
          endDate={filters.endDate}
          tabIndex={tabIndex}
          rowsPerPage={rowsPerPage}
          page={page}
          data={data as TCheckoutOrderPagination}
          internationalSalesEnabled={internationalSalesEnabled}
          currency={filters.currency}
          onChangeCurrency={(currency) => setFilters({ ...filters, currency })}
          handleOpenFilter={handleOpenFilter}
          handleTabChange={handleTabChange}
          handleConvertToCSV={handleConvertToCSV}
          handleConvertToXLS={handleConvertToXLS}
          handleChangeItem={handleChangeItem}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
      {(!isSmallScreen || type === 'abandoned') && (
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignContent="space-between"
            alignItems="center"
            sx={{ mb: 2, px: 2 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <TitlePageTypography>Transações</TitlePageTypography>
              <SubtitlePageTypography>
                {`Filtro do período de ${dateDescription(filters.startDate)} à ${dateDescription(filters.endDate)}`}
              </SubtitlePageTypography>
            </Box>

            {!isSmallScreen && (
              <Stack direction={'row'} spacing={1} alignItems={'center'}>
                <Typography
                  fontFamily="Montserrat"
                  fontSize="14px"
                  fontWeight={500}
                  lineHeight="18px"
                  color="rgba(152, 156, 155, 1)"
                >
                  Registros por página
                </Typography>
                <Select
                  variant="outlined"
                  value={rowsPerPage}
                  onChange={(event) =>
                    handleChangeRowsPerPage(
                      NumberFunctions.toNumber(event.target.value)
                    )
                  }
                  sx={{ height: 40, width: 85 }}
                >
                  {[10, 20, 50, 100].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            )}

            {!isSmallScreen && (
              <TextField
                size="small"
                onChange={(e) => setSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    if (isReconciliation) {
                      setReconciliationPage(0)
                      reconciliation.execute()
                    } else {
                      execute()
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        alt="Pesquisar"
                        src="/assets/icons/search/search.svg"
                      />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    ml: 4.5,
                    color: 'black',
                  },
                  shrink: search !== '',
                  required: false,
                }}
                sx={(theme) => ({
                  backgroundColor: 'white',
                  '& .Mui-focused .MuiInputAdornment-root': {
                    color: theme.palette.primary.main,
                  },
                  '& .Mui-error .MuiInputAdornment-root': {
                    color: theme.palette.error.main,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    px: 5.5,
                  },
                })}
                label="Pesquisar"
              />
            )}

            {!isSmallScreen && !isReconciliation && (
              <SelectInput
                width="180px"
                value={report}
                onChange={(e) =>
                  setReport(e.target.value as TCheckoutOrderReport)
                }
                options={OrderFunctions.STATUS_FILTER}
              />
            )}

            <Button
              sx={{
                p: 0,
                m: 0,
                minWidth: 0,
                borderRadius: '8px',
              }}
              onClick={handleOpenFilter}
            >
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid #C5C6C9',
                  borderRadius: '8px',
                  p: '8px',
                  fontSize: '0rem',
                  height: 40,
                }}
              >
                <img
                  src="/assets/icons/menu/funnel/funnelBlack.svg"
                  alt="Filtro"
                />
              </Paper>
            </Button>
          </Stack>

          {type !== 'abandoned' &&
            !isReconciliation &&
            internationalSalesEnabled && (
              <Stack direction="row" sx={{ mb: 2, px: 2 }}>
                <CurrencyToggle
                  value={filters.currency}
                  onChange={(currency) => setFilters({ ...filters, currency })}
                />
              </Stack>
            )}

          {isReconciliation ? (
            <AnticipationReconciliationTotals
              data={reconciliation.data}
              error={reconciliation.error}
              clear={reconciliation.clear}
            />
          ) : (
            <>
              {isTenantBank() && error !== '' && (
                <Box sx={{ mb: 2 }}>
                  <AlertDefault
                    onClose={clear}
                    severity="error"
                    sx={{ width: '100%' }}
                  >
                    {error}
                  </AlertDefault>
                </Box>
              )}

              {!isTenantBank() && error !== '' && (
                <AlertDefault
                  severity="warning"
                  variant="standard"
                  sx={{ mb: 2 }}
                >
                  <AlertTitle>Dados bancário não fornecido</AlertTitle>
                  Para vender é necessário informar uma conta bancária em{' '}
                  <i>Menu - Configurações - Dados Bancário</i>
                </AlertDefault>
              )}

              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={3}
                sx={{ mb: 2, px: { xs: 2, lg: 0 } }}
              >
                <SimpleCard sx={{ px: 4 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#F2F2FF' }}>
                      <img alt="Vendas" src="/assets/icons/payments/pay.svg" />
                    </Avatar>
                    <Stack direction="column">
                      <Typography
                        variant="body2"
                        color="#707275"
                        fontWeight={500}
                        fontSize="14px"
                        lineHeight="14px"
                      >
                        Transações encontradas
                      </Typography>
                      <Typography
                        variant="h4"
                        color="#38393B"
                        fontWeight={600}
                        fontSize="24px"
                        lineHeight="32px"
                      >
                        {data.total}
                      </Typography>
                    </Stack>
                  </Stack>
                </SimpleCard>
                {type !== 'abandoned' && internationalSalesEnabled && (
                  <DualCurrencyTotals
                    info={(data as TCheckoutOrderPagination).info}
                    infoUsd={(data as TCheckoutOrderPagination).infoUsd}
                  />
                )}
                {type !== 'abandoned' && !internationalSalesEnabled && (
                  <SimpleCard sx={{ px: 4 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{ width: 40, height: 40, bgcolor: '#F2F2FF' }}
                      >
                        <img
                          alt="Vendas"
                          src="/assets/icons/payments/pay.svg"
                        />
                      </Avatar>
                      <Stack direction="column">
                        <Typography
                          variant="body2"
                          color="#707275"
                          fontWeight={500}
                          fontSize="14px"
                          lineHeight="14px"
                        >
                          Valor líquido
                        </Typography>
                        <Typography
                          variant="h4"
                          color="#38393B"
                          fontWeight={600}
                          fontSize="24px"
                          lineHeight="32px"
                        >
                          {NumberFunctions.formatMoneyDefault(
                            NumberFunctions.toNumber(data.info)
                          )}
                        </Typography>
                      </Stack>
                    </Stack>
                  </SimpleCard>
                )}
              </Stack>
            </>
          )}

          <Stack
            direction="row"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: isSmallScreen ? 2 : 0,
            }}
            alignItems="center"
          >
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="basic tabs example"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flexGrow: isSmallScreen ? 0 : 1,
                width: isSmallScreen ? windowSize[0] : '100%',
              }}
            >
              <Tab label="Aprovadas" {...a11yProps(CHECKOUT_TAB_APPROVED)} />
              <Tab label="Todas" {...a11yProps(CHECKOUT_TAB_ALL)} />
              <Tab label="Abertas" {...a11yProps(CHECKOUT_TAB_OPENED)} />
              <Tab label="Abandonadas" {...a11yProps(CHECKOUT_TAB_ABANDONED)} />
              <Tab label="Reembolso" {...a11yProps(CHECKOUT_TAB_CANCELED)} />
              {isSysAdmin && (
                <Tab
                  label="Reconciliação de antecipação"
                  {...a11yProps(CHECKOUT_TAB_RECONCILIATION)}
                />
              )}
              {isSmallScreen && !isReconciliation && (
                <Stack
                  height="46px"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ExportDialog
                    reportType={reportType}
                    setReportType={setReportType}
                    allowSplit={type === 'approved'}
                    allowExpectedRelease={type === 'approved'}
                    onExport={async ({ fileType }) => {
                      if (fileType === 'xls') await handleConvertToXLS()
                      else await handleConvertToCSV()
                    }}
                  />
                </Stack>
              )}
            </Tabs>
            {!isSmallScreen && !isReconciliation && (
              <ExportDialog
                reportType={reportType}
                setReportType={setReportType}
                allowSplit={type === 'approved'}
                allowExpectedRelease={type === 'approved'}
                onExport={async ({ fileType }) => {
                  if (fileType === 'xls') await handleConvertToXLS()
                  else await handleConvertToCSV()
                }}
              />
            )}
          </Stack>

          {isReconciliation && (
            <AnticipationReconciliationTab
              data={reconciliation.data}
              page={reconciliationPage}
              rowsPerPage={rowsPerPage}
              onPageChange={setReconciliationPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
          {!isReconciliation && (
            <DataTable<TCheckoutOrder | TLostSale>
              key={type}
              columns={
                dataTableColumns[type] as IDataTableColumn<
                  TCheckoutOrder | TLostSale
                >[]
              }
              hasNewCustomHeader
              data={data}
              page={page}
              rowsPerPage={rowsPerPage}
              onItemClick={
                ![CHECKOUT_TAB_OPENED, CHECKOUT_TAB_ABANDONED].includes(
                  tabIndex
                )
                  ? handleChangeItem
                  : undefined
              }
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              renderItemMobile={renderItemMobile}
            />
          )}
        </Box>
      )}
      <CheckoutOrderDetails
        checkoutOrder={current}
        open={open}
        isOpened={tabIndex === CHECKOUT_TAB_OPENED}
        handleClose={handleClose}
        setOpen={setOpen}
        setSuccess={setSuccess}
        setError={setErrorSending}
        execute={execute}
        setCopied={setCopied}
      />

      {(type !== 'abandoned' || isReconciliation) && (
        <AdvancedFilters
          open={openFilter}
          setOpen={setOpenFilter}
          affiliateds={affiliateds}
          products={products}
          handleCloseFilter={handleCloseFilter}
          startDateDefault={addDays(-29)}
          endDateDefault={new Date()}
          onlyRangeDate={type === 'opened' && !isReconciliation}
          filters={filters}
          onChangeFilters={setFilters}
          hasStatusFilter={type === 'all' && !isReconciliation}
          hasTypeFilter={!isReconciliation}
          hasTrackerFilter={!isReconciliation}
        />
      )}

      {type === 'abandoned' && !isReconciliation && (
        <AbandonedAdvancedFilters
          open={openFilter}
          lastDays={filters.lastDays ?? 30}
          setOpen={setOpenFilter}
          handleCloseFilter={handleCloseFilter}
          setLastDays={(lastDays) => setFilters({ ...filters, lastDays })}
        />
      )}

      <LoadingBackdrop
        open={
          loading ||
          reconciliation.loading ||
          downloading ||
          loadingProducts ||
          loadingAffiliateds
        }
      />

      <ErrorSnackbar
        open={errorSending !== ''}
        onClose={() => setErrorSending('')}
      >
        {errorSending}
      </ErrorSnackbar>

      <SuccessSnackbar open={success} onClose={() => setSuccess(false)}>
        Concluído com sucesso
      </SuccessSnackbar>

      <SuccessSnackbar open={copied} onClose={() => setCopied(false)}>
        Copiado com sucesso
      </SuccessSnackbar>

      <AsaasSeal sx={{ mt: 3 }} />
    </>
  )
}
